import { createQueryClient } from "@khazzin/api-client/query";
import { TooltipProvider } from "@khazzin/ui/primitives/Display";
import { QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { type ReactNode, useMemo } from "react";
import { createBrowserRouter, Navigate, type RouteObject, RouterProvider } from "react-router";
import { ApiClientProvider } from "./api";
import { impersonationToken } from "./app/shell/impersonation";
import { PortalLocaleProvider } from "./app/shell/locale";
import { InboxScreen, Landing, MorePage, NotFound, ServiceGate, Shell } from "./app/shell/Shell";
import { StoreScopeProvider } from "./app/shell/scope";
import { AnalyticsPage } from "./modules/analytics/AnalyticsPage";
import { CatalogPage } from "./modules/catalog/CatalogPage";
import { DashboardPage } from "./modules/dashboard/DashboardPage";
import { DeliveryRatesPage } from "./modules/delivery_rates/DeliveryRatesPage";
import { FinancePage } from "./modules/finance/FinancePage";
import { StatementPage } from "./modules/finance/StatementPage";
import { InventoryPage } from "./modules/inventory/InventoryPage";
import { EditOrderPage, NewOrderPage } from "./modules/new_order/NewOrderPage";
import { OrderDetailPage } from "./modules/orders/OrderDetailPage";
import { OrdersPage } from "./modules/orders/OrdersPage";
import { ReturnsPage } from "./modules/returns/ReturnsPage";
import { MyNotificationsPage } from "./modules/settings/MyNotificationsPage";
import { SettingsPage } from "./modules/settings/SettingsPage";
import { AcceptInvitationPage } from "./modules/team/AcceptInvitationPage";
import { TeamPage } from "./modules/team/TeamPage";
import { TicketDetailPage } from "./modules/tickets/TicketDetailPage";
import { TicketsPage } from "./modules/tickets/TicketsPage";

/**
 * The Store Portal's providers and its router (EP10-S01, EP10-S02).
 *
 * The query cache, an API client that names this app and the active store on
 * every request, the language, and the store scope — then the shell, with one
 * route per section. Each section's screen replaces its placeholder in its own
 * story.
 */
export const portalRoutes: RouteObject[] = [
	// EP10-S10: outside the shell, because the person redeeming a link may not
	// belong to any store yet.
	{ path: "/invitations/accept", element: <AcceptInvitationPage /> },
	{
		path: "/",
		element: <Shell />,
		children: [
			{ index: true, element: <Landing /> },
			// EP10-S03. Every section has its screen now; an address none of
			// these routes names is not found (ADJ-M03-4).
			{ path: "orders", element: <OrdersPage /> },
			{ path: "orders/:orderId", element: <OrderDetailPage /> },
			// EP10-S05: the New Order section, and an order's form to edit or resubmit.
			{ path: "orders/:orderId/edit", element: <EditOrderPage /> },
			{
				path: "new-order",
				element: (
					<ServiceGate section="new_order">
						<NewOrderPage />
					</ServiceGate>
				),
			},
			// EP10-S11: Settings's five tabs, one route each (EP12-S16's
			// notification preferences are the Notifications tab).
			{ path: "settings/:tab?", element: <SettingsPage /> },
			// EP10-S08: Finance, a statement with its ledger drill-down, and Delivery rates.
			{ path: "finance", element: <FinancePage /> },
			{ path: "finance/statements/:statementId", element: <StatementPage /> },
			{
				path: "delivery-rates",
				element: (
					<ServiceGate section="delivery_rates">
						<DeliveryRatesPage />
					</ServiceGate>
				),
			},
			// EP10-S10: Team & Access.
			{ path: "team", element: <TeamPage /> },
			// EP10-S04: the External Import section is a tab of Orders.
			{ path: "external-import", element: <Navigate to="/orders?tab=external_import" replace /> },
			// ADJ-R01-4: the Integrations entry is Settings' API keys tab, which
			// gates on the integrations level; the bare section was a placeholder.
			{ path: "integrations", element: <Navigate to="/settings/api-keys" replace /> },
			// EP10-S06: Inventory's four tabs, one route each.
			{ path: "inventory/:tab?", element: <InventoryPage /> },
			// EP10-S14: Returns — the store's cases in four tabs, and Approve RTO.
			{
				path: "returns",
				element: (
					<ServiceGate section="returns">
						<ReturnsPage />
					</ServiceGate>
				),
			},
			// EP10-S13: the home tiles and the store's Sales & Revenue.
			{ path: "dashboard", element: <DashboardPage /> },
			{ path: "analytics", element: <AnalyticsPage /> },
			// EP10-S07: Catalog — items and SKUs, approval, a package per SKU.
			{ path: "catalog", element: <CatalogPage /> },
			{ path: "more", element: <MorePage /> },
			// EP12-S03: the bell's "View all".
			{ path: "inbox", element: <InboxScreen /> },
			// EP10-S12: tickets and claims, and every account's own notification
			// preferences — neither is one of the thirteen sections.
			{ path: "tickets", element: <TicketsPage /> },
			{ path: "tickets/:ticketId", element: <TicketDetailPage /> },
			{ path: "notifications", element: <MyNotificationsPage /> },
			{ path: "*", element: <NotFound /> },
		],
	},
];

export function App() {
	const queryClient = useMemo(createQueryClient, []);
	const router = useMemo(() => createBrowserRouter(portalRoutes), []);

	return (
		<QueryClientProvider client={queryClient}>
			<ApiClientProvider>
				<PortalLocaleProvider>
					<SignedInScope>
						{/* As in the console: the notification matrix and charts carry tooltips. */}
						<TooltipProvider delayDuration={300}>
							<RouterProvider router={router} />
						</TooltipProvider>
					</SignedInScope>
				</PortalLocaleProvider>
			</ApiClientProvider>
		</QueryClientProvider>
	);
}

/** The store scope, which asks for `/me/context` once somebody is signed in. */
function SignedInScope({ children }: { children: ReactNode }) {
	const { user, isLoading } = useAuth();
	// A Login as Store tab (EP01-S16) is signed in by its token.
	const signedIn = impersonationToken() !== null || (user !== null && !isLoading);
	return <StoreScopeProvider enabled={signedIn}>{children}</StoreScopeProvider>;
}
